import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../context/AuthContext";
import {
  Box,
  CircularProgress,
  Snackbar,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Container,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
const SURFACE = "#163571";
const ITEM_TYPE = "CHILD";

// ─── Inline editable label ──────────────────────────────────────────────────
const InlineEdit = ({ value, onSave, typographyProps = {} }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef();

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setDraft(trimmed || value);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          variant="standard"
          size="small"
          autoFocus
          sx={{
            "& .MuiInput-root": {
              fontFamily: '"Outfit", sans-serif',
              fontWeight: typographyProps.fontWeight || 600,
              fontSize: typographyProps.fontSize || "1rem",
              color: typographyProps.color || NAV_BG,
            },
            "& .MuiInput-underline:after": { borderColor: AMBER },
          }}
        />
        <IconButton size="small" onClick={commit} sx={{ color: AMBER, p: 0.4 }}>
          <CheckIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={cancel} sx={{ color: "#94a3b8", p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "default" }}
      onClick={() => setEditing(true)}
    >
      <Typography
        {...typographyProps}
        sx={{
          ...typographyProps.sx,
          cursor: "text",
          borderBottom: "1.5px dashed transparent",
          "&:hover": { borderBottomColor: "rgba(245,158,11,0.5)" },
          transition: "border-color 0.15s",
          lineHeight: 1.3,
        }}
      >
        {value}
      </Typography>
      <Tooltip title={t('departments.rename')} arrow>
        <EditIcon
          sx={{
            fontSize: 13,
            color: "rgba(0,0,0,0.25)",
            opacity: 0,
            transition: "opacity 0.15s",
            ".inline-edit-row:hover &": { opacity: 1 },
          }}
        />
      </Tooltip>
    </Box>
  );
};

// ─── Child draggable card ────────────────────────────────────────────────────
const ChildCard = ({ child }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { child_id: child.id, firstname: child.firstname, lastname: child.lastname },
    collect: (m) => ({ isDragging: m.isDragging() }),
  }));

  return (
    <Box
      ref={drag}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 0.75,
        mb: 0.5,
        borderRadius: "8px",
        background: isDragging ? "rgba(245,158,11,0.08)" : "white",
        border: "1px solid",
        borderColor: isDragging ? AMBER : "rgba(15,45,94,0.1)",
        cursor: "grab",
        opacity: isDragging ? 0.45 : 1,
        userSelect: "none",
        transition: "all 0.15s",
        "&:hover": {
          borderColor: "rgba(245,158,11,0.5)",
          background: "rgba(245,158,11,0.04)",
          transform: "translateX(2px)",
        },
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 14, color: "#cbd5e1", flexShrink: 0 }} />
      <PersonIcon sx={{ fontSize: 14, color: AMBER_DARK, flexShrink: 0 }} />
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "0.8rem",
          fontWeight: 500,
          color: NAV_BG,
          lineHeight: 1.2,
        }}
      >
        {child.firstname} {child.lastname}
      </Typography>
    </Box>
  );
};

// ─── Department droppable box ────────────────────────────────────────────────
const DepartmentBox = ({ dept, onDrop, onRename, onDelete }) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item) => onDrop(item, dept.id),
    collect: (m) => ({ isOver: m.isOver() }),
  }));

  return (
    <Box
      ref={drop}
      sx={{
        borderRadius: "12px",
        background: isOver
          ? "linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(245,158,11,0.05) 100%)"
          : "rgba(248,250,252,0.8)",
        border: "1.5px solid",
        borderColor: isOver ? AMBER : "rgba(15,45,94,0.1)",
        p: 1.5,
        minHeight: 120,
        transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: isOver ? `0 0 0 3px ${AMBER_GLOW}` : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Dept header */}
      <Box
        className="inline-edit-row"
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
          <GroupsIcon sx={{ fontSize: 14, color: AMBER_DARK, flexShrink: 0 }} />
          <InlineEdit
            value={dept.name}
            onSave={(name) => onRename(dept.id, name)}
            typographyProps={{
              sx: { fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: "0.82rem", color: "#334155" },
            }}
          />
          <Chip
            label={dept.children.length}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 700,
              background: dept.children.length > 0 ? `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` : "#e2e8f0",
              color: dept.children.length > 0 ? NAV_BG : "#94a3b8",
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>
        <Tooltip title={t('departments.delete_dept')} arrow>
          <IconButton
            size="small"
            onClick={() => onDelete(dept.id)}
            sx={{
              p: 0.3,
              color: "#cbd5e1",
              "&:hover": { color: "#f87171", background: "rgba(248,113,113,0.08)" },
              transition: "all 0.15s",
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Children list */}
      <Box sx={{ flex: 1 }}>
        {dept.children.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 60,
              borderRadius: "8px",
              border: "1.5px dashed",
              borderColor: isOver ? AMBER : "rgba(15,45,94,0.12)",
              transition: "border-color 0.18s",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "0.72rem",
                color: isOver ? AMBER_DARK : "#94a3b8",
                fontStyle: "italic",
              }}
            >
              {isOver ? t('departments.release_to_drop') : t('departments.drop_here')}
            </Typography>
          </Box>
        ) : (
          dept.children.map((child) => <ChildCard key={child.id} child={child} />)
        )}
      </Box>
    </Box>
  );
};

// ─── SchoolClass section ─────────────────────────────────────────────────────
const SchoolClassSection = ({ sc, onDrop, onRenameClass, onDeleteClass, onRenameDept, onDeleteDept, onAddDept }) => {
  const { t } = useTranslation();
  const totalChildren = sc.departments.reduce((n, d) => n + d.children.length, 0);

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: "16px",
        background: "white",
        border: "1px solid rgba(15,45,94,0.1)",
        boxShadow: "0 2px 16px rgba(15,45,94,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Class header bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${NAV_BG} 0%, ${SURFACE} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box className="inline-edit-row" sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 2px 8px ${AMBER_GLOW}`,
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 16, color: NAV_BG }} />
          </Box>
          <InlineEdit
            value={sc.name}
            onSave={(name) => onRenameClass(sc.id, name)}
            typographyProps={{
              sx: {
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 700,
                fontSize: "1rem",
                color: "#f1f5f9",
                letterSpacing: "-0.01em",
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
            <Chip
              label={t('departments.n_depts', { count: sc.departments.length })}
              size="small"
              sx={{
                height: 20,
                fontFamily: '"Outfit", sans-serif',
                fontSize: "0.68rem",
                fontWeight: 600,
                background: "rgba(255,255,255,0.12)",
                color: "rgba(241,245,249,0.85)",
                "& .MuiChip-label": { px: 1 },
              }}
            />
            <Chip
              label={t('departments.n_students', { count: totalChildren })}
              size="small"
              sx={{
                height: 20,
                fontFamily: '"Outfit", sans-serif',
                fontSize: "0.68rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                color: NAV_BG,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={t('departments.add_dept')} arrow>
            <IconButton
              size="small"
              onClick={() => onAddDept(sc.id)}
              sx={{
                color: "rgba(245,158,11,0.7)",
                border: "1px solid rgba(245,158,11,0.25)",
                borderRadius: "8px",
                p: 0.6,
                "&:hover": { background: "rgba(245,158,11,0.12)", color: AMBER },
                transition: "all 0.15s",
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('departments.delete_class')} arrow>
            <IconButton
              size="small"
              onClick={() => onDeleteClass(sc.id)}
              sx={{
                color: "rgba(248,113,113,0.5)",
                border: "1px solid rgba(248,113,113,0.2)",
                borderRadius: "8px",
                p: 0.6,
                "&:hover": { background: "rgba(248,113,113,0.1)", color: "#f87171" },
                transition: "all 0.15s",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Departments grid */}
      <Box sx={{ p: 2.5 }}>
        {sc.departments.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              borderRadius: "12px",
              border: "1.5px dashed rgba(15,45,94,0.12)",
              gap: 1,
            }}
          >
            <GroupsIcon sx={{ fontSize: 28, color: "#cbd5e1" }} />
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.82rem", color: "#94a3b8" }}>
              {t('departments.no_depts')}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onAddDept(sc.id)}
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                color: AMBER_DARK,
                "&:hover": { background: AMBER_GLOW },
              }}
            >
              {t('departments.add_dept')}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
              gap: 1.5,
            }}
          >
            {sc.departments.map((dept) => (
              <DepartmentBox
                key={dept.id}
                dept={dept}
                onDrop={onDrop}
                onRename={onRenameDept}
                onDelete={onDeleteDept}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Delete confirm dialog ───────────────────────────────────────────────────
const ConfirmDialog = ({ open, title, body, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{
      sx: {
        borderRadius: "16px",
        boxShadow: "0 24px 64px rgba(15,45,94,0.2)",
        border: "1px solid rgba(15,45,94,0.08)",
        minWidth: 340,
      },
    }}
  >
    <DialogTitle
      sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: "1.05rem", color: NAV_BG, pb: 0.5 }}
    >
      {title}
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.875rem", color: "#64748b" }}>
        {body}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
      <Button
        onClick={onCancel}
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 500,
          textTransform: "none",
          color: "#64748b",
          "&:hover": { background: "#f1f5f9" },
        }}
      >
        {t('common.cancel')}
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "8px",
          background: "#ef4444",
          boxShadow: "none",
          "&:hover": { background: "#dc2626", boxShadow: "none" },
        }}
      >
        {t('common.delete')}
      </Button>
    </DialogActions>
  </Dialog>
  );
};

// ─── Add name dialog ─────────────────────────────────────────────────────────
const AddNameDialog = ({ open, title, placeholder, onConfirm, onCancel, saving = false }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(""); }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(15,45,94,0.2)",
          border: "1px solid rgba(15,45,94,0.08)",
          minWidth: 360,
        },
      }}
    >
      <DialogTitle
        sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: "1.05rem", color: NAV_BG, pb: 0.5 }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        <TextField
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim() && !saving) onConfirm(name.trim()); }}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: '"Outfit", sans-serif',
              borderRadius: "10px",
              "&.Mui-focused fieldset": { borderColor: AMBER },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onCancel}
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 500,
            textTransform: "none",
            color: "#64748b",
            "&:hover": { background: "#f1f5f9" },
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => name.trim() && !saving && onConfirm(name.trim())}
          variant="contained"
          disabled={!name.trim() || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG,
            boxShadow: `0 4px 14px ${AMBER_GLOW}`,
            "&:hover": { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, boxShadow: `0 6px 20px rgba(245,158,11,0.35)` },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
          }}
        >
          {t('departments.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Dept reassign & delete dialog ──────────────────────────────────────────
const DeptReassignDialog = ({ open, dept, otherDepts, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState('');

  useEffect(() => {
    if (open) setTargetId(otherDepts.length > 0 ? String(otherDepts[0].id) : '');
  }, [open, otherDepts]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(15,45,94,0.2)',
          border: '1px solid rgba(15,45,94,0.08)',
          minWidth: 380,
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.05rem', color: NAV_BG, pb: 0.5 }}>
        {t('departments.reassign_title', { name: dept?.name })}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#64748b', mb: 2 }}>
          {t('departments.reassign_students', { count: dept?.children?.length ?? 0 })}
        </Typography>
        {otherDepts.length === 0 ? (
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#ef4444' }}>
            {t('departments.no_other_depts')}
          </Typography>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('departments.move_to')}</InputLabel>
            <Select
              value={targetId}
              label={t('departments.move_to')}
              onChange={(e) => setTargetId(e.target.value)}
              sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '10px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}
            >
              {otherDepts.map((d) => (
                <MuiMenuItem key={d.id} value={String(d.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>
                  {d.name}
                </MuiMenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onCancel} sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 500, textTransform: 'none', color: '#64748b', '&:hover': { background: '#f1f5f9' } }}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => onConfirm(targetId)}
          variant="contained"
          disabled={otherDepts.length === 0 || !targetId}
          sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: '#ef4444', boxShadow: 'none', '&:hover': { background: '#dc2626', boxShadow: 'none' } }}
        >
          {t('departments.move_and_delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const DepartmentsPage = () => {
  const { authTokens, backendUrl, pageTitle } = useContext(AuthContext);
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // snackbar
  const [snack, setSnack] = useState({ open: false, severity: "success", text: "" });
  const showSnack = (severity, text) => setSnack({ open: true, severity, text });

  // dialogs
  const [addClassOpen, setAddClassOpen] = useState(false);
  const [addDeptFor, setAddDeptFor] = useState(null); // schoolclass_id
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, name }
  const [deptSaving, setDeptSaving] = useState(false);
  const [deptReassignDialog, setDeptReassignDialog] = useState(null); // { dept, otherDepts }

  document.title = (pageTitle || "SB2") + " - " + t('departments.title');

  const headers = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: "Bearer " + String(authTokens.access),
    }),
    [authTokens]
  );

  // ── Load ──
  const loadClasses = useCallback(async () => {
    try {
      const res = await fetch(backendUrl + "api/get-classes/", { headers: headers() });
      if (!res.ok) throw new Error();
      setClasses(await res.json());
    } catch {
      showSnack("error", t('departments.failed_load'));
    } finally {
      setLoading(false);
    }
  }, [backendUrl, headers]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // ── Drag & drop ──
  const handleDrop = useCallback(
    async (child, targetDeptId) => {
      setClasses((prev) =>
        prev.map((sc) => ({
          ...sc,
          departments: sc.departments.map((d) => ({
            ...d,
            children:
              d.id === targetDeptId
                ? d.children.some((c) => c.id === child.child_id)
                  ? d.children
                  : [...d.children, { id: child.child_id, firstname: child.firstname, lastname: child.lastname }]
                : d.children.filter((c) => c.id !== child.child_id),
          })),
        }))
      );
      try {
        const res = await fetch(backendUrl + `api/child/${child.child_id}/department/`, {
          method: "PATCH",
          headers: headers(),
          body: JSON.stringify({ department_id: targetDeptId }),
        });
        if (!res.ok) throw new Error();
      } catch {
        showSnack("error", t('departments.failed_move'));
        loadClasses();
      }
    },
    [backendUrl, headers, loadClasses]
  );

  // ── Create class ──
  const handleCreateClass = async (name) => {
    setAddClassOpen(false);
    try {
      const res = await fetch(backendUrl + "api/schoolclass-create/", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setClasses((prev) => [...prev, created]);
      showSnack("success", t('departments.class_created', { name }));
    } catch {
      showSnack("error", t('departments.failed_create_class'));
    }
  };

  // ── Rename class ──
  const handleRenameClass = async (id, name) => {
    try {
      const res = await fetch(backendUrl + `api/schoolclass-rename/${id}/`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error();
      setClasses((prev) => prev.map((sc) => (sc.id === id ? { ...sc, name } : sc)));
    } catch {
      showSnack("error", t('departments.failed_rename_class'));
    }
  };

  // ── Delete class ──
  const handleDeleteClass = (id) => {
    const sc = classes.find((c) => c.id === id);
    setConfirmDelete({ type: "class", id, name: sc?.name });
  };

  const executeDeleteClass = async () => {
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      const res = await fetch(backendUrl + `api/schoolclass-delete/${id}/`, {
        method: "DELETE",
        headers: headers(),
      });
      if (!res.ok) throw new Error();
      setClasses((prev) => prev.filter((sc) => sc.id !== id));
      showSnack("success", t('departments.class_deleted'));
    } catch {
      showSnack("error", t('departments.failed_delete_class'));
    }
  };

  // ── Add department ──
  const handleAddDept = (schoolclassId) => setAddDeptFor(schoolclassId);

  const handleCreateDept = async (name) => {
    const scId = addDeptFor;
    setDeptSaving(true);
    try {
      const res = await fetch(backendUrl + "api/department-create/", {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ name, schoolclass_id: scId }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setClasses((prev) =>
        prev.map((sc) => (sc.id === scId ? { ...sc, departments: [...sc.departments, created] } : sc))
      );
      showSnack("success", t('departments.dept_created', { name }));
    } catch {
      showSnack("error", t('departments.failed_create_dept'));
    } finally {
      setDeptSaving(false);
      setAddDeptFor(null);
    }
  };

  // ── Rename department ──
  const handleRenameDept = async (id, name) => {
    try {
      const res = await fetch(backendUrl + `api/department-rename/${id}/`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error();
      setClasses((prev) =>
        prev.map((sc) => ({
          ...sc,
          departments: sc.departments.map((d) => (d.id === id ? { ...d, name } : d)),
        }))
      );
    } catch {
      showSnack("error", t('departments.failed_rename_dept'));
    }
  };

  // ── Delete department ──
  const handleDeleteDept = (id) => {
    const dept = classes.flatMap((sc) => sc.departments).find((d) => d.id === id);
    if (dept && dept.children.length > 0) {
      const otherDepts = classes.flatMap((sc) => sc.departments).filter((d) => d.id !== id);
      setDeptReassignDialog({ dept, otherDepts });
    } else {
      setConfirmDelete({ type: "department", id, name: dept?.name });
    }
  };

  const executeDeleteDept = async () => {
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      const res = await fetch(backendUrl + `api/department-delete/${id}/`, {
        method: "DELETE",
        headers: headers(),
      });
      if (!res.ok) throw new Error();
      setClasses((prev) =>
        prev.map((sc) => ({ ...sc, departments: sc.departments.filter((d) => d.id !== id) }))
      );
      showSnack("success", t('departments.dept_deleted'));
    } catch {
      showSnack("error", t('departments.failed_delete_dept'));
    }
  };

  const executeMoveAndDeleteDept = async (targetDeptId) => {
    const { dept } = deptReassignDialog;
    setDeptReassignDialog(null);
    try {
      // Move all children to the target department
      await Promise.all(dept.children.map((child) =>
        fetch(backendUrl + `api/child/${child.id}/department/`, {
          method: "PATCH",
          headers: headers(),
          body: JSON.stringify({ department_id: targetDeptId }),
        })
      ));
      // Delete the department
      const res = await fetch(backendUrl + `api/department-delete/${dept.id}/`, {
        method: "DELETE",
        headers: headers(),
      });
      if (!res.ok) throw new Error();
      showSnack("success", t('departments.moved_and_deleted'));
      loadClasses();
    } catch {
      showSnack("error", t('departments.failed_move_delete'));
      loadClasses();
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
          <Typography
            sx={{ mt: 2, fontFamily: '"Outfit", sans-serif', fontSize: "0.875rem", color: "#64748b" }}
          >
            {t('departments.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  const totalStudents = classes.flatMap((sc) => sc.departments).flatMap((d) => d.children).length;

  return (
    <DndProvider backend={HTML5Backend}>
      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={20000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          variant="filled"
          elevation={6}
          sx={{ width: "100%", fontFamily: '"Outfit", sans-serif', borderRadius: "10px" }}
          severity={snack.severity}
        >
          {snack.text}
        </MuiAlert>
      </Snackbar>

      {/* Dialogs */}
      <AddNameDialog
        open={addClassOpen}
        title={t('departments.new_class')}
        placeholder={t('departments.new_class_placeholder')}
        onConfirm={handleCreateClass}
        onCancel={() => setAddClassOpen(false)}
      />
      <AddNameDialog
        open={Boolean(addDeptFor)}
        title={t('departments.new_dept_title')}
        placeholder={t('departments.new_dept_placeholder')}
        onConfirm={handleCreateDept}
        onCancel={() => setAddDeptFor(null)}
        saving={deptSaving}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={confirmDelete?.type === "class" ? t('departments.delete_class_title') : t('departments.delete_dept_title')}
        body={t('departments.delete_confirm', { name: confirmDelete?.name })}
        onConfirm={confirmDelete?.type === "class" ? executeDeleteClass : executeDeleteDept}
        onCancel={() => setConfirmDelete(null)}
      />
      <DeptReassignDialog
        open={Boolean(deptReassignDialog)}
        dept={deptReassignDialog?.dept}
        otherDepts={deptReassignDialog?.otherDepts || []}
        onConfirm={executeMoveAndDeleteDept}
        onCancel={() => setDeptReassignDialog(null)}
      />

      {/* Page */}
      <Box sx={{ minHeight: "100vh", background: "#f1f5f9", pt: 4, pb: 8 }}>
        <Container maxWidth="xl">

          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`,
                  boxShadow: "0 4px 14px rgba(15,45,94,0.25)",
                  flexShrink: 0,
                }}
              >
                <AccountTreeIcon sx={{ color: AMBER, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 700,
                    fontSize: "1.5rem",
                    color: NAV_BG,
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {t('departments.title')}
                </Typography>
                <Typography
                  sx={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.8rem", color: "#94a3b8", mt: 0.1 }}
                >
                  {t('departments.n_classes', { count: classes.length })} · {t('departments.n_students', { count: totalStudents })} · {t('departments.drag_hint')}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddClassOpen(true)}
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                color: NAV_BG,
                boxShadow: `0 4px 14px ${AMBER_GLOW}`,
                border: "none",
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                "&:hover": {
                  background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`,
                  boxShadow: "0 6px 20px rgba(245,158,11,0.35)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {t('departments.new_class')}
            </Button>
          </Box>

          {/* Empty state */}
          {classes.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 10,
                borderRadius: "20px",
                background: "white",
                border: "1.5px dashed rgba(15,45,94,0.15)",
                gap: 1.5,
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
              <Typography
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: "1rem", color: "#64748b" }}
              >
                {t('departments.no_classes')}
              </Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.82rem", color: "#94a3b8" }}>
                {t('departments.no_classes_hint')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddClassOpen(true)}
                sx={{
                  mt: 1,
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                  color: NAV_BG,
                  boxShadow: `0 4px 14px ${AMBER_GLOW}`,
                }}
              >
                {t('departments.new_class')}
              </Button>
            </Box>
          ) : (
            classes.map((sc) => (
              <SchoolClassSection
                key={sc.id}
                sc={sc}
                onDrop={handleDrop}
                onRenameClass={handleRenameClass}
                onDeleteClass={handleDeleteClass}
                onRenameDept={handleRenameDept}
                onDeleteDept={handleDeleteDept}
                onAddDept={handleAddDept}
              />
            ))
          )}
        </Container>
      </Box>
    </DndProvider>
  );
};

export default DepartmentsPage;
